// ─── SIDEBAR 3.0 — MISSION COMMAND™ ─────────────────────────────────────────
// Sprint: SIDEBAR 3.0 — Complete redesign. Not a refinement.
// This is Mission Command — not a nav drawer.
//
// ARCHITECTURE:
//   MissionCommandHeader — cinematic hero + avatar + greeting + progress
//   NavCard              — 72px glass navigation card
//   SystemSection        — collapsible system info
//   BottomBar            — minimal sync + version + sign out
//   Sidebar              — main export, assembles everything
//
// LOCATION-AWARE HERO:
//   Reads from RoamerLocationEngine. City name + time shown as overlay.
//   City-specific photography lookup table — falls back to global hero.
//   Crossfades on location change (700–900ms).

import { Link, useLocation } from 'wouter';
import { useEffect, useState, useRef, useCallback } from 'react';
import {
  Home, Target, DollarSign, Zap,
  BookOpen, Globe, Package, UserCircle, Settings,
  Cloud, LogOut, ChevronDown, ChevronRight, ShieldCheck,
  MapPin, Clock, Wifi, GraduationCap,
} from 'lucide-react';
import { useIsOwner, useIsBetaTester, useMCVisible } from '@/hooks/useRole';
import { useGetOpportunities } from '@/lib/use-opportunities';
import { loadPersona } from '@/services/OperatorPersonaService';
import { buildLivePaymentData } from '@/services/PaymentIntelligenceService';
import { loadExecutionState, getActiveMissionCounts } from '@/services/MissionExecutionEngine';
import { roamerLocationEngine } from '@/services/RoamerLocationEngine';
import { useCoreEvents } from '@/hooks/useCoreEvents';
import { BUILD_NUMBER, BUILD_DATE } from '@/lib/build-version';
import { storageManager, CLOUD_SYNC_EVENT } from '@/services/storage/StorageManager';

// ─── Design tokens ────────────────────────────────────────────────────────────

const C = {
  bg:       '#050B16',
  card:     '#0D1627',
  border:   'rgba(255,255,255,0.06)',
  primary:  '#21B6FF',
  secondary:'#8C5DFF',
  success:  '#28D76F',
  warning:  '#FFB547',
  danger:   '#FF5C5C',
  text:     '#E8EFF8',
  muted:    'rgba(255,255,255,0.45)',
  faint:    'rgba(255,255,255,0.20)',
  panelW:   280,
  iconRailW: 56,
} as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getGreeting(): string {
  const h = new Date().getHours();
  if (h >= 5  && h < 12) return 'Good Morning';
  if (h >= 12 && h < 17) return 'Good Afternoon';
  if (h >= 17 && h < 21) return 'Good Evening';
  return 'Good Night';
}

function formatMemberSince(iso: string): string {
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }); }
  catch { return ''; }
}

function formatLocalTime(): string {
  return new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function useMobile(): boolean {
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setMobile(e.matches);
    setMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return mobile;
}

// ─── Location-aware hero image lookup ─────────────────────────────────────────
// Curated set of specific Unsplash photos for major nomad hubs.
// Falls back to the global WorkRoamer hero on error or unknown city.

const BASE = (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '');
const FALLBACK_HERO = `${BASE}/sidebar-roam-header.png`;

// Map lowercase city/country keywords → Unsplash photo IDs (curated, high-quality)
const CITY_HERO_MAP: Record<string, string> = {
  'chiang mai':    'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=560&q=80&fit=crop',
  'bangkok':       'https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=560&q=80&fit=crop',
  'bali':          'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=560&q=80&fit=crop',
  'ubud':          'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=560&q=80&fit=crop',
  'lisbon':        'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=560&q=80&fit=crop',
  'porto':         'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=560&q=80&fit=crop',
  'tokyo':         'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=560&q=80&fit=crop',
  'osaka':         'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=560&q=80&fit=crop',
  'singapore':     'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=560&q=80&fit=crop',
  'ho chi minh':   'https://images.unsplash.com/photo-1583417267826-aebc4d1542e1?w=560&q=80&fit=crop',
  'hanoi':         'https://images.unsplash.com/photo-1583417267826-aebc4d1542e1?w=560&q=80&fit=crop',
  'medellin':      'https://images.unsplash.com/photo-1586155016374-8af8b3b6e40c?w=560&q=80&fit=crop',
  'barcelona':     'https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=560&q=80&fit=crop',
  'berlin':        'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=560&q=80&fit=crop',
  'amsterdam':     'https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=560&q=80&fit=crop',
  'cape town':     'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=560&q=80&fit=crop',
  'dubai':         'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=560&q=80&fit=crop',
  'prague':        'https://images.unsplash.com/photo-1541849546-216549ae216d?w=560&q=80&fit=crop',
  'budapest':      'https://images.unsplash.com/photo-1541849546-216549ae216d?w=560&q=80&fit=crop',
  'london':        'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=560&q=80&fit=crop',
  'paris':         'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=560&q=80&fit=crop',
  'new york':      'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=560&q=80&fit=crop',
  'tucson':        'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=560&q=80&fit=crop',
  'tbilisi':       'https://images.unsplash.com/photo-1590736704728-f4730bb30770?w=560&q=80&fit=crop',
  'mexico city':   'https://images.unsplash.com/photo-1585464231875-d9ef1f5ad396?w=560&q=80&fit=crop',
};

function resolveHeroUrl(city: string, country: string): string {
  const key = city.toLowerCase();
  const countryKey = country.toLowerCase();
  for (const [mapKey, url] of Object.entries(CITY_HERO_MAP)) {
    if (key.includes(mapKey) || mapKey.includes(key)) return url;
    if (countryKey.includes(mapKey)) return url;
  }
  return FALLBACK_HERO;
}

// ─── Sidebar data hook ────────────────────────────────────────────────────────

interface SidebarData {
  displayName: string;
  avatarUrl: string | null;
  memberSince: string;
  todayEarned: number;
  dailyGoal: number;
  weekEarnings: number;
  activeMissions: number;
  oppCount: number;
  city: string;
  country: string;
  heroUrl: string;
  syncHealth: 'healthy' | 'degraded' | 'offline';
  syncLabel: string;
  queuedWrites: number;
}

function useSidebarData(): SidebarData {
  const [data, setData] = useState<SidebarData>({
    displayName: 'Roamer', avatarUrl: null, memberSince: '',
    todayEarned: 0, dailyGoal: 25, weekEarnings: 0,
    activeMissions: 0, oppCount: 0,
    city: '', country: '', heroUrl: FALLBACK_HERO,
    syncHealth: 'offline', syncLabel: 'Offline', queuedWrites: 0,
  });

  const busVersion = useCoreEvents(
    ['SystemStateChanged', 'LocationChanged', 'MissionCompleted', 'PaymentReceived'],
    'sidebar-v3',
  );

  const refresh = useCallback(() => {
    const persona = loadPersona();
    const exState = loadExecutionState();
    const counts  = getActiveMissionCounts(exState);
    const monthly = persona?.incomeGoalMonthly ?? 750;
    let payData: ReturnType<typeof buildLivePaymentData> | null = null;
    try { payData = buildLivePaymentData(monthly); } catch { payData = null; }

    const loc   = roamerLocationEngine.getCurrentLocation();
    const city    = loc.city ?? '';
    const country = loc.country ?? '';
    const heroUrl = resolveHeroUrl(city, country);

    let diag: ReturnType<typeof storageManager.getDiagnostics> | null = null;
    try { diag = storageManager.getDiagnostics(); } catch { diag = null; }
    const syncHealth = diag?.syncHealth ?? 'offline';
    const syncLabel  = syncHealth === 'healthy' ? 'Synced'
                     : syncHealth === 'degraded' ? `Syncing (${diag?.queuedWrites ?? 0} queued)`
                     : 'Offline';

    // Today's earnings: approximate as week / 7 when no daily field exists
    const weekEarned = payData?.thisWeek?.earned ?? 0;
    const todayEarned = weekEarned > 0 ? Math.round((weekEarned / 7) * 10) / 10 : 0;
    const dailyGoal   = Math.round((monthly / 30) * 10) / 10 || 25;

    setData({
      displayName:    persona?.displayName ?? 'Roamer',
      avatarUrl:      persona?.avatarDataUrl ?? null,
      memberSince:    persona?.createdAt ? formatMemberSince(persona.createdAt) : '',
      todayEarned,
      dailyGoal,
      weekEarnings:   weekEarned,
      activeMissions: counts.started + counts.applied,
      oppCount:       0, // refreshed separately below
      city,
      country,
      heroUrl,
      syncHealth,
      syncLabel,
      queuedWrites:   diag?.queuedWrites ?? 0,
    });
  }, []);

  // Opportunity count — reads from the shared React Query cache (same key as
  // useGetOpportunities / Home.tsx). Auto-updates after every pipeline refresh
  // because Home.tsx invalidates OPPORTUNITIES_QUERY_KEY on completion.
  // Previously: a raw fetch('/api/opportunities?limit=1') that always returned 0
  // because the endpoint returns a bare array and d?.total / d?.count don't exist.
  const { data: oppsData } = useGetOpportunities();
  useEffect(() => {
    setData(prev => ({ ...prev, oppCount: oppsData?.length ?? 0 }));
  }, [oppsData]);

  useEffect(() => { refresh(); }, [busVersion, refresh]);

  // Also refresh on cloud sync events
  useEffect(() => {
    window.addEventListener(CLOUD_SYNC_EVENT, refresh);
    window.addEventListener('online', refresh);
    window.addEventListener('offline', refresh);
    return () => {
      window.removeEventListener(CLOUD_SYNC_EVENT, refresh);
      window.removeEventListener('online', refresh);
      window.removeEventListener('offline', refresh);
    };
  }, [refresh]);

  return data;
}

// ─── Status pulse dot ─────────────────────────────────────────────────────────

function PulseDot({ color }: { color: string }) {
  return (
    <span style={{
      display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
      background: color, flexShrink: 0,
      boxShadow: `0 0 0 0 ${color}`,
      animation: 'mcPulse 2.4s ease-in-out infinite',
    }} />
  );
}

// ─── MissionCommandHeader ─────────────────────────────────────────────────────

interface HeaderProps {
  data: SidebarData;
  greeting: string;
  localTime: string;
  onNavigate: (href: string) => void;
}

function MissionCommandHeader({ data, greeting, localTime, onNavigate }: HeaderProps) {
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [heroSrc, setHeroSrc]       = useState(data.heroUrl);
  const [fadeKey, setFadeKey]       = useState(0);

  // Crossfade when hero URL changes
  useEffect(() => {
    if (data.heroUrl === heroSrc) return;
    setHeroLoaded(false);
    const t = setTimeout(() => {
      setHeroSrc(data.heroUrl);
      setFadeKey(k => k + 1);
    }, 200);
    return () => clearTimeout(t);
  }, [data.heroUrl, heroSrc]);

  const pct = data.dailyGoal > 0
    ? Math.min(100, Math.round((data.todayEarned / data.dailyGoal) * 100))
    : 0;

  const syncColor = data.syncHealth === 'healthy' ? C.success
                  : data.syncHealth === 'degraded' ? C.warning
                  : '#94a3b8';

  return (
    <div style={{ position: 'relative', width: '100%', height: 260, overflow: 'hidden', flexShrink: 0 }}>
      {/* Cinematic hero image with crossfade */}
      <img
        key={fadeKey}
        src={heroSrc}
        alt=""
        aria-hidden="true"
        onLoad={() => setHeroLoaded(true)}
        onError={() => { setHeroSrc(FALLBACK_HERO); setHeroLoaded(true); }}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit: 'cover', objectPosition: 'center',
          opacity: heroLoaded ? 1 : 0,
          transition: 'opacity 0.8s ease',
        }}
      />
      {/* Fallback bg while image loads */}
      <div style={{ position: 'absolute', inset: 0, background: '#060e20' }} />

      {/* 65% dark overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, rgba(5,11,22,0.55) 0%, rgba(5,11,22,0.72) 55%, rgba(5,11,22,0.92) 100%)',
      }} />

      {/* Content */}
      <div style={{
        position: 'relative', zIndex: 2, height: '100%',
        display: 'flex', flexDirection: 'column',
        padding: '14px 16px 16px',
        animation: 'mcFadeIn 0.5s ease forwards',
      }}>

        {/* Top row: WR logo + location/time */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'auto' }}>
          <img
            src={`${BASE}/workroamer-brand.png`}
            alt="WorkRoamer™"
            style={{ height: 24, width: 'auto', display: 'block', filter: 'brightness(1.05)' }}
          />
          {data.city ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <MapPin style={{ width: 9, height: 9, color: C.primary, flexShrink: 0 }} />
                <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.90)', letterSpacing: '0.02em' }}>
                  {data.city}{data.country ? `, ${data.country}` : ''}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, color: 'rgba(255,255,255,0.50)', fontSize: 9 }}>
                <Clock style={{ width: 8, height: 8 }} />
                {localTime}
              </div>
            </div>
          ) : null}
        </div>

        {/* Avatar + greeting */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <button
            onClick={() => onNavigate('/profile')}
            aria-label="Edit profile"
            style={{
              width: 64, height: 64, borderRadius: '50%',
              border: `2.5px solid ${C.primary}`,
              boxShadow: `0 0 0 4px rgba(33,182,255,0.12), 0 0 24px rgba(33,182,255,0.25)`,
              overflow: 'hidden', background: 'rgba(13,22,39,0.80)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', padding: 0, flexShrink: 0,
              transition: 'box-shadow 0.2s, border-color 0.2s',
            }}
          >
            {data.avatarUrl
              ? <img src={data.avatarUrl} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <UserCircle style={{ width: 34, height: 34, color: 'rgba(255,255,255,0.55)' }} />}
          </button>

          <div style={{ textAlign: 'center', lineHeight: 1 }}>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.60)', fontWeight: 500, marginBottom: 3 }}>
              {greeting},
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: '0.01em' }}>
              {data.displayName}
            </div>
          </div>

          {/* READY TO ROAM badge */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'rgba(40,215,111,0.12)', border: `1px solid rgba(40,215,111,0.35)`,
            borderRadius: 99, padding: '4px 12px',
            fontSize: 10, fontWeight: 700, color: C.success,
            letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            <PulseDot color={C.success} />
            READY TO ROAM
          </div>
        </div>

        {/* Today's Mission progress */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 9.5, fontWeight: 700, color: 'rgba(255,255,255,0.50)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Today's Mission
            </span>
            <span style={{ fontSize: 10, fontWeight: 700, color: C.primary }}>
              ${data.todayEarned} / ${data.dailyGoal}
            </span>
          </div>
          {/* Glowing progress bar */}
          <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.10)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 2,
              width: `${pct}%`,
              background: `linear-gradient(90deg, ${C.primary}, ${C.secondary})`,
              boxShadow: `0 0 8px ${C.primary}80`,
              transition: 'width 1.2s cubic-bezier(0.16, 1, 0.3, 1)',
            }} />
          </div>

          {/* Member since + sync */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 2 }}>
            {data.memberSince ? (
              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', display: 'flex', alignItems: 'center', gap: 3 }}>
                <ShieldCheck style={{ width: 9, height: 9 }} />
                {data.memberSince}
              </span>
            ) : <span />}
            <span style={{ fontSize: 9, color: syncColor, display: 'flex', alignItems: 'center', gap: 3 }}>
              <Wifi style={{ width: 9, height: 9 }} />
              {data.syncLabel}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── NavCard ──────────────────────────────────────────────────────────────────

interface NavItem {
  href:      string;
  emoji:     string;
  icon:      React.ReactNode;
  title:     string;
  subtitle:  string;
  badge?:    string;
  badgeColor?: string;
}

function NavCard({
  item, isActive, onClick,
}: { item: NavItem; isActive: boolean; onClick?: () => void }) {
  const [hov, setHov] = useState(false);

  const accentAlpha = isActive ? '0.14' : hov ? '0.07' : '0.00';
  const borderColor = isActive ? C.primary : hov ? 'rgba(255,255,255,0.10)' : C.border;

  const inner = (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        height: 72, padding: '0 14px',
        borderRadius: 14, marginBottom: 6,
        background: `rgba(33,182,255,${accentAlpha})`,
        border: `1px solid ${borderColor}`,
        cursor: 'pointer',
        transition: 'background 0.15s, border-color 0.15s',
        animation: 'mcSlideUp 0.3s ease forwards',
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Active left accent line */}
      {isActive && (
        <div style={{
          position: 'absolute', left: 0, top: '20%', bottom: '20%',
          width: 3, borderRadius: '0 2px 2px 0',
          background: C.primary,
          boxShadow: `0 0 10px ${C.primary}`,
        }} />
      )}

      {/* Icon */}
      <div style={{
        width: 42, height: 42, borderRadius: 12, flexShrink: 0,
        background: isActive
          ? `linear-gradient(135deg, ${C.primary}33, ${C.secondary}22)`
          : 'rgba(255,255,255,0.05)',
        border: `1px solid ${isActive ? C.primary + '40' : 'rgba(255,255,255,0.08)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: isActive ? C.primary : 'rgba(255,255,255,0.55)',
        transition: 'all 0.15s',
        fontSize: 20,
      }}>
        {item.emoji ? (
          <span style={{ fontSize: 20, lineHeight: 1 }}>{item.emoji}</span>
        ) : item.icon}
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14, fontWeight: 700,
          color: isActive ? '#fff' : 'rgba(255,255,255,0.80)',
          letterSpacing: '0.01em', lineHeight: 1.2, marginBottom: 3,
        }}>
          {item.title}
        </div>
        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.3 }}>
          {item.subtitle}
        </div>
      </div>

      {/* Badge */}
      {item.badge && (
        <div style={{
          flexShrink: 0, padding: '3px 8px', borderRadius: 99,
          background: `${item.badgeColor ?? C.primary}18`,
          border: `1px solid ${item.badgeColor ?? C.primary}40`,
          fontSize: 9.5, fontWeight: 700,
          color: item.badgeColor ?? C.primary,
          letterSpacing: '0.04em', textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}>
          {item.badge}
        </div>
      )}

      {/* Chevron */}
      <ChevronRight style={{
        width: 14, height: 14, flexShrink: 0,
        color: isActive ? C.primary : 'rgba(255,255,255,0.20)',
        transition: 'color 0.15s',
      }} />
    </div>
  );

  return <Link href={item.href} onClick={onClick}>{inner}</Link>;
}

// ─── SystemSection (collapsible) ──────────────────────────────────────────────

function SystemSection({ onLogout }: { onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const [diag, setDiag] = useState(() => {
    try { return storageManager.getDiagnostics(); } catch { return null; }
  });

  useEffect(() => {
    const refresh = () => { try { setDiag(storageManager.getDiagnostics()); } catch { /* keep last */ } };
    window.addEventListener(CLOUD_SYNC_EVENT, refresh);
    return () => window.removeEventListener(CLOUD_SYNC_EVENT, refresh);
  }, []);

  const sync = diag?.syncHealth ?? 'offline';
  const syncColor = sync === 'healthy' ? C.success : sync === 'degraded' ? C.warning : '#94a3b8';
  const syncLabel = sync === 'healthy' ? 'Synced'
                  : sync === 'degraded' ? `Syncing (${diag?.queuedWrites ?? 0} pending)`
                  : 'Offline';

  return (
    <div style={{ marginBottom: 6 }}>
      {/* Collapsible header */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 4px', background: 'transparent', border: 'none', cursor: 'pointer',
          color: C.muted,
        }}
      >
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          ▾ System
        </span>
        {open
          ? <ChevronDown style={{ width: 12, height: 12 }} />
          : <ChevronRight style={{ width: 12, height: 12 }} />}
      </button>

      {open && (
        <div style={{
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: 12, padding: '10px 12px',
          display: 'flex', flexDirection: 'column', gap: 8,
          animation: 'mcFadeIn 0.2s ease',
        }}>
          {/* Cloud Sync */}
          <Row label="Cloud Sync">
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: syncColor }}>
              <PulseDot color={syncColor} />
              {syncLabel}
            </span>
          </Row>
          {/* Connector Health */}
          <Row label="Connector Health">
            <span style={{ color: C.success, display: 'flex', alignItems: 'center', gap: 5 }}>
              <PulseDot color={C.success} />
              Operational
            </span>
          </Row>
          {/* Version */}
          <Row label="Version">
            <span style={{ color: C.text }}>v{BUILD_NUMBER}</span>
          </Row>
          {/* Build date */}
          <Row label="Last Update">
            <span style={{ color: C.muted }}>
              {new Date(`${BUILD_DATE}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </Row>
          {/* Beta */}
          <Row label="Beta Information">
            <span style={{
              background: `${C.secondary}20`, border: `1px solid ${C.secondary}40`,
              color: C.secondary, fontSize: 9, fontWeight: 700,
              padding: '2px 6px', borderRadius: 4, letterSpacing: '0.06em',
            }}>BETA</span>
          </Row>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11 }}>
      <span style={{ color: C.muted }}>{label}</span>
      {children}
    </div>
  );
}

// ─── BottomBar ────────────────────────────────────────────────────────────────

function BottomBar({ onLogout, isLoggingOut, data }: { onLogout: () => void; isLoggingOut: boolean; data: SidebarData }) {
  const syncColor = data.syncHealth === 'healthy' ? C.success
                  : data.syncHealth === 'degraded' ? C.warning
                  : '#94a3b8';

  return (
    <div style={{
      borderTop: `1px solid ${C.border}`, padding: '12px 14px 16px',
      flexShrink: 0, background: C.bg,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {/* Status row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Cloud sync */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10 }}>
          <Cloud style={{ width: 11, height: 11, color: syncColor }} />
          <span style={{ color: syncColor, fontWeight: 600 }}>
            {data.syncHealth === 'healthy' ? 'Synced' : data.syncLabel}
          </span>
        </div>
        {/* Version */}
        <span style={{ fontSize: 9.5, color: C.faint, fontFamily: 'monospace' }}>
          v{BUILD_NUMBER}
        </span>
        {/* System status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10 }}>
          <PulseDot color={data.syncHealth !== 'offline' ? C.success : '#94a3b8'} />
          <span style={{ color: data.syncHealth !== 'offline' ? C.success : '#94a3b8', fontWeight: 600 }}>
            {data.syncHealth !== 'offline' ? 'Operational' : 'Offline'}
          </span>
        </div>
      </div>

      {/* Sign Out */}
      <button
        onClick={isLoggingOut ? undefined : onLogout}
        disabled={isLoggingOut}
        aria-busy={isLoggingOut}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          padding: '10px 16px', borderRadius: 10,
          background: isLoggingOut ? `${C.danger}08` : 'transparent',
          border: `1.5px solid ${isLoggingOut ? C.danger + '35' : C.danger + '60'}`,
          cursor: isLoggingOut ? 'not-allowed' : 'pointer',
          color: isLoggingOut ? `${C.danger}70` : C.danger,
          fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
          transition: 'background 0.15s, border-color 0.15s, opacity 0.15s',
          opacity: isLoggingOut ? 0.7 : 1,
        }}
        onMouseEnter={e => {
          if (isLoggingOut) return;
          e.currentTarget.style.background = `${C.danger}12`;
          e.currentTarget.style.borderColor = `${C.danger}90`;
        }}
        onMouseLeave={e => {
          if (isLoggingOut) return;
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = `${C.danger}60`;
        }}
      >
        <LogOut style={{ width: 14, height: 14, animation: isLoggingOut ? 'spin 1s linear infinite' : 'none' }} />
        {isLoggingOut ? 'Signing out…' : 'Sign Out'}
      </button>

      {/* About link — tap sequence in /about unlocks Mission Control */}
      <Link href="/about">
        <div style={{
          textAlign: 'center', fontSize: 10, color: 'rgba(255,255,255,0.15)',
          cursor: 'pointer', padding: '4px 0', letterSpacing: '0.06em',
          transition: 'color 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.15)'; }}
        >
          About WorkRoamer™
        </div>
      </Link>
    </div>
  );
}

// ─── Collapsed icon rail (desktop only) ───────────────────────────────────────

function CollapsedRail({
  location, destinations, onExpand, onLogout, isLoggingOut,
}: {
  location: string;
  destinations: NavItem[];
  onExpand: () => void;
  onLogout: () => void;
  isLoggingOut: boolean;
}) {
  return (
    <>
      <div className="hidden md:flex" aria-hidden="true" style={{ width: C.iconRailW, flexShrink: 0 }} />
      <aside style={{
        position: 'fixed', top: 0, left: 0, bottom: 0,
        width: C.iconRailW, zIndex: 50,
        display: 'flex', flexDirection: 'column',
        background: C.bg, borderRight: `1px solid ${C.border}`,
      }}>
        <div style={{ height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <button
            onClick={onExpand}
            aria-label="Expand sidebar"
            style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 7px', cursor: 'pointer', color: C.muted, display: 'flex', alignItems: 'center' }}
          >
            <ChevronRight style={{ width: 14, height: 14 }} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 6px', display: 'flex', flexDirection: 'column', gap: 4, scrollbarWidth: 'none' }}>
          {destinations.map(d => {
            const active = location === d.href;
            return (
              <Link key={d.href} href={d.href}>
                <div title={d.title} style={{
                  width: 40, height: 40, borderRadius: 10, margin: '0 auto',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', fontSize: 18,
                  background: active ? `rgba(33,182,255,0.14)` : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${active ? C.primary + '50' : 'transparent'}`,
                  transition: 'all 0.15s',
                }}>
                  {d.emoji}
                </div>
              </Link>
            );
          })}
        </div>
        <div style={{ padding: 8, borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
          <button
            onClick={isLoggingOut ? undefined : onLogout}
            disabled={isLoggingOut}
            aria-busy={isLoggingOut}
            title={isLoggingOut ? 'Signing out…' : 'Sign Out'}
            style={{
              width: '100%', display: 'flex', justifyContent: 'center', padding: 8,
              borderRadius: 8, background: 'transparent', border: 'none',
              cursor: isLoggingOut ? 'not-allowed' : 'pointer',
              color: isLoggingOut ? `${C.danger}60` : C.danger,
              opacity: isLoggingOut ? 0.6 : 1,
              transition: 'opacity 0.15s',
            }}
          >
            <LogOut style={{ width: 14, height: 14, animation: isLoggingOut ? 'spin 1s linear infinite' : 'none' }} />
          </button>
        </div>
      </aside>
    </>
  );
}

// ─── Main Sidebar Component ───────────────────────────────────────────────────

interface SidebarProps {
  userEmail: string;
  onLogout: () => void;
  isLoggingOut?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ onLogout, isLoggingOut = false, isOpen, onClose }: SidebarProps) {
  const [location, navigate] = useLocation();
  const [collapsed, setCollapsed]   = useState(false);
  const [localTime, setLocalTime]   = useState(formatLocalTime);
  const isMobile     = useMobile();
  const data         = useSidebarData();
  const greeting     = getGreeting();
  const isOwner      = useIsOwner();
  const isBetaTester = useIsBetaTester();
  const mcVisible    = useMCVisible();

  // Clock tick — drives the local time display in the header
  useEffect(() => {
    const id = setInterval(() => setLocalTime(formatLocalTime()), 10_000);
    return () => clearInterval(id);
  }, []);

  // Body scroll lock when mobile sidebar is open
  useEffect(() => {
    document.body.style.overflow = (isMobile && isOpen) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobile, isOpen]);

  const handleNavigate = useCallback((href: string) => {
    navigate(href);
    onClose();
  }, [navigate, onClose]);

  const handleNavClick = useCallback(() => {
    if (!isMobile) return; // desktop: let Link handle it, don't close
    onClose();
  }, [isMobile, onClose]);

  // ── Nav items ───────────────────────────────────────────────────────────────
  // Spec: 8 primary items + settings

  // ── Role-aware nav items ─────────────────────────────────────────────────────
  // Standard items every user sees. Elevated items added conditionally below.
  const navItems: NavItem[] = [
    {
      href: '/home', emoji: '🏠', icon: <Home size={20} />,
      title: 'Home',
      subtitle: 'Mission Dashboard',
    },
    {
      href: '/companies', emoji: '🎯', icon: <Target size={20} />,
      title: 'Today',
      subtitle: "Today's Mission",
      badge: data.activeMissions > 0 ? 'ACTIVE' : undefined,
      badgeColor: C.success,
    },
    {
      href: '/income', emoji: '💰', icon: <DollarSign size={20} />,
      title: 'Earn',
      subtitle: 'Income Center · Wallet · Payments',
      badge: data.todayEarned > 0 ? `$${data.todayEarned} Today` : '$0 Today',
      badgeColor: C.warning,
    },
    {
      href: '/opportunities', emoji: '🚀', icon: <Zap size={20} />,
      title: 'Opportunities',
      subtitle: 'Roamer Ranked · Updated recently',
      badge: data.oppCount > 0 ? `${data.oppCount} Available` : undefined,
      badgeColor: C.success,
    },
    {
      href: '/opportunity-preferences', emoji: '🎓', icon: <GraduationCap size={20} />,
      title: 'Roamer Academy™',
      subtitle: 'Career Guides · Learning · Interests',
    },
    {
      href: '/learn-to-roam', emoji: '🧠', icon: <BookOpen size={20} />,
      title: 'Vault',
      subtitle: 'Roamer Vault™ · Knowledge · Collections',
    },
    {
      href: '/network', emoji: '🤝', icon: <Globe size={20} />,
      title: 'Network',
      subtitle: 'Peers · Connections · Meetups',
    },
    {
      href: '/passport', emoji: '🌍', icon: <Globe size={20} />,
      title: 'Journey',
      subtitle: 'Passport™ · XP · Achievements',
    },
    {
      href: '/profile', emoji: '👤', icon: <UserCircle size={20} />,
      title: 'Profile',
      subtitle: 'Goals · Preferences · Account',
    },
    {
      href: '/settings', emoji: '⚙️', icon: <Settings size={20} />,
      title: 'Settings',
      subtitle: 'Preferences · Schedule · Connectors',
    },
  ];

  // Beta Lab — visible to BETA_TESTER and above
  if (isBetaTester && !mcVisible) {
    navItems.push({
      href: '/beta-lab', emoji: '🧪', icon: <Settings size={20} />,
      title: 'Beta Lab',
      subtitle: 'Testing · Feedback · Diagnostics',
    });
  }

  // Mission Control — OWNER only, and only after session PIN unlock
  if (mcVisible) {
    navItems.push({
      href: '/mission-control', emoji: '🛸', icon: <Settings size={20} />,
      title: 'Mission Control',
      subtitle: 'Platform Ops · Developer Tools',
      badge: 'OWNER',
      badgeColor: '#8C5DFF',
    });
  }

  // ── Collapsed icon rail (desktop only) ──────────────────────────────────────
  if (collapsed && !isMobile) {
    return (
      <CollapsedRail
        location={location}
        destinations={navItems}
        onExpand={() => setCollapsed(false)}
        onLogout={onLogout}
        isLoggingOut={isLoggingOut}
      />
    );
  }

  // ── Main panel ──────────────────────────────────────────────────────────────
  const panel = (
    <div style={{
      width: C.panelW, flexShrink: 0,
      display: 'flex', flexDirection: 'column', height: '100%',
      background: C.bg, overflowX: 'hidden',
      borderRight: `1px solid ${C.border}`,
    }}>
      {/* Mission Command Header */}
      <MissionCommandHeader
        data={data}
        greeting={greeting}
        localTime={localTime}
        onNavigate={handleNavigate}
      />

      {/* Collapse button (desktop only) — sits just below the header */}
      {!isMobile && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '6px 12px 0', flexShrink: 0 }}>
          <button
            onClick={() => setCollapsed(true)}
            title="Collapse sidebar"
            aria-label="Collapse sidebar"
            style={{
              background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`,
              borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
              fontSize: 9.5, color: C.muted, letterSpacing: '0.06em',
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            ‹‹ Collapse
          </button>
        </div>
      )}

      {/* Primary nav */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 4px', scrollbarWidth: 'none' }}>
        {navItems.map((item, idx) => (
          <div
            key={item.href}
            style={{ animationDelay: `${idx * 40}ms` }}
          >
            <NavCard
              item={item}
              isActive={location === item.href || (item.href !== '/home' && location.startsWith(item.href))}
              onClick={handleNavClick}
            />
          </div>
        ))}

        {/* System section */}
        <div style={{ padding: '4px 2px' }}>
          <SystemSection onLogout={onLogout} />
        </div>

        {/* Dev: Audit Report link */}
        {import.meta.env.DEV && (
          <Link href="/audit" onClick={handleNavClick}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', marginBottom: 4,
              border: `1px dashed rgba(255,255,255,0.08)`, borderRadius: 6, cursor: 'pointer',
              color: location === '/audit' ? C.primary : 'rgba(148,163,184,0.35)',
              fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.08em', textTransform: 'uppercase',
              transition: 'color 0.15s',
            }}>
              📋 Audit Report
            </div>
          </Link>
        )}
      </div>

      {/* Bottom bar */}
      <BottomBar onLogout={onLogout} isLoggingOut={isLoggingOut} data={data} />
    </div>
  );

  // ── MOBILE: overlay drawer ──────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div
          className={`fixed inset-0 z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
          style={{ background: 'rgba(0,0,0,0.70)' }}
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Sidebar panel */}
        <aside style={{
          position: 'fixed', top: 0, left: 0, bottom: 0,
          width: C.panelW, zIndex: 50,
          transform: isOpen ? 'translateX(0)' : `translateX(-${C.panelW}px)`,
          transition: 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
        }}>
          {panel}
        </aside>
      </>
    );
  }

  // ── DESKTOP ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="hidden md:flex" aria-hidden="true" style={{ width: C.panelW, flexShrink: 0 }} />
      <aside style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: C.panelW, zIndex: 50 }}>
        {panel}
      </aside>
    </>
  );
}
